import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/src/renderer/contexts/AuthProvider'
import { usePlatform } from '@/src/renderer/hooks/usePlatform'
import { OnboardingProgressDots } from '../../components/OnboardingProgressDots'
import { Button } from '@/src/renderer/components/ui/Button'
import { BlurredSingleBackground } from '@/src/renderer/components/ui/BlurredSingleBackground'
import { StayFocusedSlide } from './StayFocusedSlide'
import { PauseAndRewardSlide } from './PauseAndRewardSlide'
import { NotEverythingSlide } from './NotEverythingSlide'
import { GetStartedSlide } from './GetStartedSlide'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { markIntroNew } from '@/src/renderer/lib/intro'
import './OnboardingPage.scss'

/**
 * OnboardingPage Component
 *
 * 3-screen onboarding carousel with:
 * - Slide 0: Stay Focused (Fuchsia theme)
 * - Slide 1: Reward Yourself (Violet theme)
 * - Slide 2: Get Started (Green theme)
 *
 * Features:
 * - Keyboard navigation (Arrow keys, Enter)
 * - Window resize to 412x396px on mount
 * - Completion tracking (localStorage for offline, database for registered users)
 * - No skip button - users must complete all slides
 */
export const OnboardingPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const account = useAccountSource()
  const source = useDataSource()
  const platform = usePlatform()
  const [currentSlide, setCurrentSlide] = useState(0)
  const totalSlides = 4

  // Window sizing is handled by separate windows in main process

  const handleNext = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1)
    }
  }, [currentSlide, totalSlides])

  const handlePrevious = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1)
    }
  }, [currentSlide])

  const handleComplete = useCallback(async () => {

    // Mark onboarding as completed
    const loginMethod = localStorage.getItem('loginMethod')

    if (loginMethod !== 'offline' && user) {
      // Registered user: save to database AND mark setup flow as completed
      await account.updateSetupProgress('completed')
      localStorage.setItem('onboarding_completed', 'true')
    } else {
      // Offline user: save to localStorage only
      localStorage.setItem('onboarding_completed', 'true')
    }

    // Die Einführung (#323) startet im Hauptfenster — es teilt die Datenbank, nicht diesen Zustand.
    // Ein Fehler hier hält niemanden vor der App auf; dann gibt es eben nur die Seitenhilfe.
    await markIntroNew(source).catch(error => console.warn('[intro] could not mark new user', error))

    // Navigate to the app
    // Use IPC to transition to separate main window
    if (window.api) {
      await window.api.invoke('transition-to-main')
    } else {
      navigate('/home')
    }
  }, [user, navigate, account, source])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && currentSlide < totalSlides - 1) {
        handleNext()
      } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
        handlePrevious()
      } else if (e.key === 'Enter') {
        if (currentSlide < totalSlides - 1) {
          handleNext()
        } else {
          handleComplete()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentSlide, totalSlides, handleNext, handlePrevious, handleComplete])

  // Track slide changes (only for cloud users)
  useEffect(() => {
    const loginMethod = localStorage.getItem('loginMethod')
    if (loginMethod !== 'offline') {
      // Update both the slide reached AND mark this slide as viewed
      void account.updateSetupProgress('onboarding', currentSlide, currentSlide)
    }
  }, [currentSlide, account])

  const getTheme = (): 'fuchsia' | 'teal' | 'pink' | 'violet' => {
    if (currentSlide === 0) return 'fuchsia'
    if (currentSlide === 1) return 'teal'
    if (currentSlide === 2) return 'pink'
    return 'violet'
  }

  const getBackgroundColor = (): string => {
    if (currentSlide === 0) return '#FFE0F5' // fuchsia
    if (currentSlide === 1) return '#CCFBF1' // teal
    if (currentSlide === 2) return '#FCE7F3' // pink
    return '#E8E0FF' // violet
  }

  const isElectron = platform.api.window !== undefined


  return (
    <div className={`onboarding-page ${isElectron ? 'onboarding-page--electron' : 'onboarding-page--web'}`}>
      <BlurredSingleBackground color={getBackgroundColor()} />

      <div className="onboarding-page__carousel-wrapper">
        <div className="onboarding-page__carousel" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
          <StayFocusedSlide />
          <PauseAndRewardSlide />
          <NotEverythingSlide />
          <GetStartedSlide />
        </div>
      </div>

      <div className="onboarding-page__footer">
        <OnboardingProgressDots currentSlide={currentSlide} totalSlides={totalSlides} theme={getTheme()} />
        {currentSlide < totalSlides - 1 ? (
          <Button variant="primary" shortcut={{ kind: 'return' }} onClick={handleNext}>{t('onboarding.continue')}</Button>
        ) : (
          <Button variant="primary" shortcut={{ kind: 'return' }} onClick={handleComplete}>{t('onboarding.getStarted.startButton')}</Button>
        )}
      </div>
    </div>
  )
}
